import React, { memo, useContext } from 'react';
import {
  IncomingIcon,
  InStockIcon,
  OutOfStockIcon,
  StockUnknownIcon,
} from '@prisjakt/ui-icons';
import { AppContext } from '@prisjakt/ui-app-context';
import * as S from './styles';
import { StockStatusStates } from './types';

type StockStatusProps = {
  className?: string;
  status?: StockStatusStates | null;
  statusText?: string;
  toneDown?: boolean;
  classicIcons?: boolean;
  showText?: boolean;
  tooltip?: string;
  width?: number;
  height?: number;
};

type StockStatusTranslationKey =
  | 'stock-status.pre_order'
  | 'stock-status.incoming'
  | 'stock-status.in_stock'
  | 'stock-status.not_in_stock'
  | 'stock-status.unknown';

const StockStatus = (props: StockStatusProps) => {
  const {
    className,
    status,
    statusText,
    toneDown,
    classicIcons,
    showText = true,
    tooltip,
    width,
    height,
  } = props;
  const { t } = useContext(AppContext);

  const getClassicIconComponent = () => {
    switch (status) {
      case 'in_stock_preorder':
      case 'incoming':
        return IncomingIcon;
      case 'in_stock':
        return InStockIcon;
      case 'not_in_stock':
        return OutOfStockIcon;
      default:
        return StockUnknownIcon;
    }
  };

  const getStatusText = (): string => {
    if (status === 'incoming' && statusText) {
      return statusText.replace(/-/g, '‑'); // Replace hyphen with hard hyphen to prevent row break in date (Eg in 2019-08-10).
    }

    return t(
      'product',
      `stock-status.${status || 'unknown'}` as StockStatusTranslationKey
    );
  };

  const getIcon = (): JSX.Element => {
    if (classicIcons) {
      const Icon = getClassicIconComponent();
      return <Icon toneDown={toneDown} width={width} height={height} />;
    }

    return <S.Icon status={status} toneDown={toneDown} />;
  };

  return (
    <S.Tooltip
      placement="left"
      title={tooltip}
      className={className}
      renderOnMouseOver
    >
      <S.IconAndText>
        {getIcon()}
        {showText && <S.Text toneDown={toneDown}>{getStatusText()}</S.Text>}
      </S.IconAndText>
    </S.Tooltip>
  );
};

export type StockStatusType = StockStatusStates;

export default memo(StockStatus);
